"use client";

/**
 * Единая карточка команды (KAN-814).
 *
 * Два варианта, потому что команду показывают в двух разных контейнерах:
 *
 *  - `tile`  — плитка каталога `/rosters`: узкая колонка в сетке. Обложка,
 *              логотип внахлёст, полоса из пяти линий и стек участников.
 *              Отвечает на вопрос «что за команда и стоит ли открывать».
 *  - `board` — широкая карточка ленты: доска `/lfg`, список команд в профиле и
 *              на публичной странице игрока. Состав раскрыт по слотам —
 *              отвечает на вопрос «кто в составе и кого не хватает».
 *
 * Третьего варианта нет намеренно: панель в профиле отличалась от карточки
 * доски только тем, что рисовала состав таблицей вместо сетки линий, а сетка
 * строго информативнее — по ней видно и людей, и дыры в составе. Всё, что было
 * ценного в таблице (ссылка на OP.GG и переход к игроку), живёт в слоте.
 */

import type { ReactNode } from "react";
import { useTranslations } from "next-intl";

import { Link } from "@/i18n/navigation";
import { cn } from "@/shared/lib/utils";
import { prettyRank } from "@/shared/lib/rank-elo";
import { getRiotRankImageUrl } from "@/shared/lib/rank-image";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/ui/data-display/avatar";
import { Badge } from "@/shared/ui/data-display/badge";
import VerifiedBadge from "@/shared/ui/data-display/verified-badge";
import { UserPlus, Users } from "lucide-react";

import { RosterLaneStrip, RosterSlotGrid } from "./roster-lineup";
import type { RosterCardData, RosterPersonView } from "./types";

/** Слотов основного состава — по числу линий. */
const LANE_SLOTS = 5;

/**
 * Декоративные градиенты шапки (дизайн-система). Детерминированно по id, чтобы
 * у команды всегда был один и тот же цвет.
 */
const CARD_GRADIENTS = [
  "--gradient-cosmos",
  "--gradient-ember",
  "--gradient-nebula",
  "--gradient-frost",
  "--gradient-toxic",
  "--gradient-dusk",
  "--gradient-abyss",
  "--gradient-gold",
] as const;

function gradientFor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return CARD_GRADIENTS[h % CARD_GRADIENTS.length] as string;
}

function initialOf(name: string): string {
  return name.trim().charAt(0).toUpperCase() || "?";
}

function membersOf(roster: RosterCardData): RosterPersonView[] {
  return [
    ...roster.lanes.flatMap((s) => (s.member ? [s.member] : [])),
    ...roster.extras,
  ];
}

function TeamTag({ tag }: { tag: string }) {
  return (
    <span className="shrink-0 rounded-sm bg-muted px-2 py-[3px] font-mono text-xs font-bold tracking-wide text-muted-foreground">
      {tag}
    </span>
  );
}

function RankChip({ rank }: { rank: string }) {
  const tUi = useTranslations("Ui");
  const img = getRiotRankImageUrl(rank);
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border bg-secondary py-[3px] pl-[5px] pr-2.5 font-heading text-xs font-bold whitespace-nowrap"
      title={tUi("litRosterCard.sredniyRangKomandy", { rank: prettyRank(rank) })}
    >
      {img ? (
        <img src={img} alt="" className="size-[22px] shrink-0 object-contain" />
      ) : null}
      {prettyRank(rank)}
    </span>
  );
}

/**
 * Бейдж поверх обложки. Обложка — произвольная картинка, поэтому контраст здесь
 * задан явно (белым по затемнению), а не токенами темы: токен на чужой картинке
 * читается через раз. Единственное место в карточке с фиксированными цветами.
 */
function LookingForPlayersOverlay() {
  const t = useTranslations("Common.roster");
  return (
    <span className="absolute right-2.5 top-2.5 inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-black/40 px-2.5 py-[3px] text-[11px] font-semibold text-white backdrop-blur-[6px]">
      <UserPlus className="size-3" />
      {t("lookingForPlayers")}
    </span>
  );
}

export type RosterCardVariant = "tile" | "board";

export interface RosterCardProps {
  roster: RosterCardData;
  variant?: RosterCardVariant;
  className?: string;
  /** Строка под названием: тип объявления, дата, счётчик — что уместно месту. */
  subtitle?: ReactNode;
  /** Действия в шапке справа: «Открыть команду», «Закрыть», счётчик участников. */
  headerActions?: ReactNode;
  /** Действие под составом: «Подать заявку», «Откликнуться». */
  footerActions?: ReactNode;
  /** Произвольный блок между составом и действиями: мета-бейджи, описание. */
  children?: ReactNode;
  onMemberClick?: ((member: RosterPersonView) => void) | undefined;
  /** LCP-образ маршрута: грузить сразу и с высоким приоритетом (KAN-977). */
  priority?: boolean;
}

export function RosterCard({
  roster,
  variant = "board",
  className,
  subtitle,
  headerActions,
  footerActions,
  children,
  onMemberClick,
  priority = false,
}: RosterCardProps) {
  const t = useTranslations("Common.roster");
  const members = membersOf(roster);
  const logo = roster.logoUrl ?? undefined;

  const nameBlock = (
    <span className="flex min-w-0 items-center gap-2">
      <span
        className={cn(
          "min-w-0 truncate font-logo leading-tight",
          variant === "tile" ? "text-lg" : "text-base",
        )}
      >
        {roster.name}
      </span>
      <VerifiedBadge isVerified={Boolean(roster.isVerified)} size="sm" />
      {roster.tag ? <TeamTag tag={roster.tag} /> : null}
    </span>
  );

  if (variant === "tile") {
    return (
      <div
        className={cn(
          "group relative flex h-full flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-[transform,box-shadow] duration-150 hover:-translate-y-0.5 hover:shadow-md",
          className,
        )}
      >
        {roster.href ? (
          <Link
            href={roster.href}
            className="absolute inset-0 z-10"
            aria-label={roster.name}
          />
        ) : null}

        <div
          className="relative h-[104px] shrink-0 overflow-hidden"
          style={
            roster.bannerUrl
              ? undefined
              : { background: `var(${gradientFor(roster.id)})` }
          }
        >
          {roster.bannerUrl ? (
            <img
              src={roster.bannerUrl}
              alt=""
              loading={priority ? "eager" : "lazy"}
              fetchPriority={priority ? "high" : undefined}
              className="absolute inset-0 size-full object-cover"
            />
          ) : null}
          {roster.isLookingForPlayers ? <LookingForPlayersOverlay /> : null}
        </div>

        <div className="flex flex-1 flex-col gap-3.5 px-4 pb-4">
          <div className="flex min-w-0 items-end gap-3">
            <span className="-mt-[26px] inline-flex shrink-0 rounded-full ring-[3px] ring-card">
              <Avatar className="size-[52px]">
                <AvatarImage src={logo} alt="" />
                <AvatarFallback className="bg-secondary text-sm font-bold">
                  {initialOf(roster.name)}
                </AvatarFallback>
              </Avatar>
            </span>
            <span className="flex min-w-0 flex-1 items-center gap-2 pb-0.5 transition-colors group-hover:text-primary">
              {nameBlock}
            </span>
          </div>

          {subtitle ? <div className="min-w-0">{subtitle}</div> : null}

          <div className="flex items-center justify-between gap-2">
            <RosterLaneStrip lanes={roster.lanes} />
            {roster.averageRank ? <RankChip rank={roster.averageRank} /> : null}
          </div>

          {children}

          <div className="mt-auto flex items-center justify-between gap-2 border-t border-border pt-3">
            {/* Пустые места стеком аватарок не рисуем: свободный слот уже виден
                в полосе линий, а серые кружки только имитировали данные. */}
            {members.length > 0 ? (
              <div className="flex items-center">
                {members.slice(0, LANE_SLOTS).map((m, i) => (
                  <Avatar
                    key={m.id ?? `${m.displayName}-${i}`}
                    className="-ml-2 size-8 shrink-0 ring-2 ring-card first:ml-0"
                  >
                    <AvatarImage src={m.avatarUrl ?? undefined} alt="" />
                    <AvatarFallback className="bg-secondary text-[11px]">
                      {initialOf(m.displayName)}
                    </AvatarFallback>
                  </Avatar>
                ))}
              </div>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <Users className="size-3.5" />
                {t("noRoster")}
              </span>
            )}
            <span className="shrink-0 font-mono text-xs text-muted-foreground">
              {Math.min(roster.memberCount ?? members.length, LANE_SLOTS)} /{" "}
              {LANE_SLOTS}
            </span>
          </div>

          {footerActions ? (
            <div className="relative z-20">{footerActions}</div>
          ) : null}
        </div>
      </div>
    );
  }

  const identity = (
    <span className="flex min-w-0 items-center gap-3">
      <Avatar className="size-10 shrink-0 rounded-xl">
        <AvatarImage src={logo} alt="" className="rounded-xl" />
        <AvatarFallback className="rounded-xl bg-muted">
          {roster.name ? (
            initialOf(roster.name)
          ) : (
            <Users className="size-4 text-muted-foreground" />
          )}
        </AvatarFallback>
      </Avatar>
      {nameBlock}
    </span>
  );

  return (
    <div
      className={cn(
        "group overflow-hidden rounded-xl border border-border bg-card shadow-sm",
        className,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-border px-4 py-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {roster.href ? (
            <Link
              href={roster.href}
              className="min-w-0 transition-colors hover:text-primary"
            >
              {identity}
            </Link>
          ) : (
            identity
          )}
          {subtitle || roster.isLookingForPlayers || roster.averageRank ? (
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              {roster.isLookingForPlayers ? (
                <Badge variant="info" className="gap-1 text-xs">
                  <UserPlus className="size-3" />
                  {t("lookingForPlayers")}
                </Badge>
              ) : null}
              {roster.averageRank ? <RankChip rank={roster.averageRank} /> : null}
              {subtitle}
            </div>
          ) : null}
        </div>
        {headerActions ? (
          <div className="flex shrink-0 items-center gap-2">{headerActions}</div>
        ) : null}
      </div>

      <div className="space-y-4 p-4">
        <RosterSlotGrid
          lanes={roster.lanes}
          extras={roster.extras}
          onMemberClick={onMemberClick}
        />
        {children}
        {footerActions}
      </div>
    </div>
  );
}
